import type { WeldAnalyticsBundle } from '../types/weld';

/** Calm copy when WeldIQ batch artefacts (or auth) are not ready. */
export const WELDIQ_ANALYTICS_NOT_READY = "Analytics isn't ready for this project yet.";

/** Calm copy when some but not all job fan-outs failed for pipeline/auth reasons. */
export const WELDIQ_SOME_JOBS_NOT_READY = "Some jobs aren't ready yet.";

/** Calm copy for live anomaly/indication scoring when WeldIQ live APIs fail soft. */
export const WELDIQ_LIVE_SCORING_UNAVAILABLE = 'Live scoring unavailable.';

/** True when the WeldIQ analytics bundle has no weld or anomaly rows to power Statistics / AI Insights. */
export function isWeldIqAnalyticsEmpty(bundle: WeldAnalyticsBundle | null | undefined): boolean {
  if (!bundle) return true;
  const hasWelds = Array.isArray(bundle.welds) && bundle.welds.length > 0;
  const hasAnomalies = Array.isArray(bundle.anomalies) && bundle.anomalies.length > 0;
  return !hasWelds && !hasAnomalies;
}

/** True when WeldIQ marked the bundle as pipeline/auth unavailable. */
export function isWeldIqUnavailable(
  bundle: WeldAnalyticsBundle | null | undefined,
): boolean {
  return Boolean(bundle?.weldIqUnavailable);
}

/**
 * Detect backend messages that mean artefacts missing, pipeline not run, or auth rejected.
 * Used to soft-degrade the dashboard instead of dumping raw S3 / API text.
 */
export function isWeldIqPipelineUnavailableMessage(msg: unknown): boolean {
  if (msg == null) return false;
  const text = String(msg).trim().toLowerCase();
  if (!text) return false;
  if (text.includes('does not exist')) return true;
  if (text.includes('run the pipeline')) return true;
  if (text.includes('not available')) return true;
  if (text.includes('pipeline data')) return true;
  if (/\b503\b/.test(text)) return true;
  if (text.includes('unauthorized')) return true;
  if (text.includes('401')) return true;
  if (text.includes('s3://') && text.includes('intermediate')) return true;
  return false;
}

/** Every fan-out failure looks like pipeline/auth unavailable (and list is non-empty). */
export function allPartialErrorsArePipelineUnavailable(
  partialErrors: { jobNumber: string; error: string }[] | undefined | null,
): boolean {
  if (!partialErrors?.length) return false;
  return partialErrors.every((e) => isWeldIqPipelineUnavailableMessage(e.error));
}

/**
 * Map backend / fan-out errors to calm user copy. Never returns s3:// paths.
 * Returns null when there is nothing to show.
 */
export function sanitizeWeldIqUserMessage(msg: unknown): string | null {
  if (msg == null) return null;
  const text = String(msg).trim();
  if (!text) return null;
  return WELDIQ_ANALYTICS_NOT_READY;
}

/** Diagnostics-only formatter; do not render in production UI. */
export function formatPartialJobErrors(
  partialErrors: { jobNumber: string; error: string }[] | undefined | null,
): string | null {
  if (!partialErrors?.length) return null;
  return partialErrors.map((e) => `${e.jobNumber}: ${e.error}`).join('; ');
}

/** User-facing partial summary — calm copy only, never backend dumps. */
export function formatPartialJobErrorsForDisplay(
  partialErrors: { jobNumber: string; error: string }[] | undefined | null,
): string | null {
  if (!partialErrors?.length) return null;
  return WELDIQ_SOME_JOBS_NOT_READY;
}

/** User-facing live scoring error — never dumps raw S3 / Unauthorized text. */
export function displayLiveScoringError(msg: unknown): string | null {
  if (msg == null) return null;
  const text = String(msg).trim();
  if (!text) return null;
  return WELDIQ_LIVE_SCORING_UNAVAILABLE;
}
